import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { mostRecentOpenList, STATUS_LABEL, statusBadgeClass } from '../../pages/groceries/display';
import { groceryKeys } from '../../pages/groceries/queries';
import { buttonClass } from '../Button';
import { EmptyState } from '../EmptyState';
import { ChevronRightIcon } from '../icons';
import { GroceriesIcon } from '../NavIcons';
import { Section, sectionLinkClass } from '../Section';
import { Skeleton } from '../Skeleton';
import { StoreNameLabel } from './StoreNameLabel';

/**
 * Dashboard card: the most recently active open/shopping grocery list, with
 * store and checked/total progress, or a prompt to start one. Self-contained
 * (its own query), matching `UpcomingMealsCard` / `ExpiringSoonCard`.
 */
export function GroceriesCard() {
  const query = { limit: 20 };
  const lists = useQuery({
    queryKey: groceryKeys.list(query),
    queryFn: () => api.groceries.list(query),
  });

  const current = mostRecentOpenList(lists.data?.lists ?? []);

  return (
    <Section
      id="groceries-card-heading"
      title="Groceries"
      accent="groceries"
      icon={<GroceriesIcon className="size-6" />}
      action={
        <Link to="/groceries" className={sectionLinkClass}>
          View all <ChevronRightIcon className="size-4" />
        </Link>
      }
    >
      {lists.isPending ? <Skeleton lines={2} label="Loading grocery lists" /> : null}
      {lists.isError ? (
        <p className="text-sm text-fail">Couldn’t load your grocery lists.</p>
      ) : null}
      {lists.isSuccess && !current ? (
        <EmptyState
          compact
          accent="groceries"
          icon={<GroceriesIcon className="size-full" />}
          title="No open lists yet"
          action={
            <Link to="/groceries" className={buttonClass({ size: 'sm', block: false })}>
              Start a list
            </Link>
          }
        />
      ) : null}

      {current ? (
        <Link
          to={`/groceries/${current.id}`}
          className="-mx-2 flex flex-col gap-2 rounded-xl px-2 py-2 transition-colors hover:bg-accent-soft/50"
        >
          <span className="flex items-center justify-between gap-3">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink">{current.name}</span>
              <span className="block text-xs text-muted">
                {current.storeId ? <StoreNameLabel storeId={current.storeId} /> : 'No store set'}
                {' · '}
                {current.checkedCount}/{current.itemCount} checked
              </span>
            </span>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(current.status)}`}
            >
              {STATUS_LABEL[current.status]}
            </span>
          </span>
          <span aria-hidden className="block h-2 overflow-hidden rounded-full bg-brand-soft">
            <span
              className="block h-full rounded-full bg-gradient-to-r from-brand to-accent"
              style={{
                width: `${current.itemCount ? Math.round((current.checkedCount / current.itemCount) * 100) : 0}%`,
              }}
            />
          </span>
        </Link>
      ) : null}
    </Section>
  );
}
